import { create } from 'zustand';

const migrateOldTemplateToNewLayout = (oldBlocks) => {
  if (!Array.isArray(oldBlocks)) return [];
  
  return oldBlocks.map((block, index) => {
    // If it's somehow already a row, return it
    if (block.type === 'row') return block;

    const rowId = `row-migrated-${Date.now()}-${index}`;
    
    // Handle the old "split" column type
    if (block.type === 'split') {
      return {
        id: rowId,
        type: 'row',
        columns: [
          {
            id: `col-migrated-left-${Date.now()}-${index}`,
            blocks: [{ ...block.left, id: block.left.id || `block-left-${Date.now()}-${index}` }]
          },
          {
            id: `col-migrated-right-${Date.now()}-${index}`,
            blocks: [{ ...block.right, id: block.right.id || `block-right-${Date.now()}-${index}` }]
          }
        ]
      };
    } 
    
    // Handle standard single blocks
    return {
      id: rowId,
      type: 'row',
      columns: [
        {
          id: `col-migrated-single-${Date.now()}-${index}`,
          blocks: [{ ...block, id: block.id || `block-migrated-${Date.now()}-${index}` }]
        }
      ]
    };
  });
};

const useBuilderStore = create((set, get) => ({
  layout: [
    {
      id: 'row-1',
      type: 'row',
      columns: [
        {
          id: 'col-1',
          blocks: [
            {
              id: 'block-1',
              type: 'image',
              src: 'https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Header',
              width: 0,
              isHero: true,
              align: 'center',
              marginTop: 0,
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
              innerPaddingTop: 0,
              innerPaddingBottom: 0,
              innerPaddingLeft: 0,
              innerPaddingRight: 0,
            },
            {
              id: 'block-2',
              type: 'heading',
              text: 'New Campaign',
              align: 'left',
              color: '#ffffff',
              fontSize: 24,
              isBold: true,
              marginTop: 0,
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
              innerPaddingTop: 32,
              innerPaddingBottom: 8,
              innerPaddingLeft: 0,
              innerPaddingRight: 0,
            },
            {
              id: 'block-3',
              type: 'text',
              text: 'Designed for modern connection.',
              align: 'left',
              color: '#a3a3a3',
              fontSize: 15,
              marginTop: 0,
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
              innerPaddingTop: 0,
              innerPaddingBottom: 16,
              innerPaddingLeft: 0,
              innerPaddingRight: 0,
            },
            {
              id: 'block-4',
              type: 'divider',
              marginTop: 0,
              marginBottom: 0,
              paddingTop: 16,
              paddingBottom: 16,
            },
            {
              id: 'block-5',
              type: 'button',
              text: 'Open App',
              url: 'https://legacyx.uk',
              align: 'center',
              bgColor: '#a855f7',
              color: '#ffffff',
              fontSize: 16,
              isBold: true,
              useGradient: true,
              gradientStart: '#a855f7',
              gradientEnd: '#4f46e5',
              marginTop: 0,
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
              innerPaddingTop: 12,
              innerPaddingBottom: 12,
              innerPaddingLeft: 24,
              innerPaddingRight: 24,
            },
          ],
        },
      ],
    },
  ],
  globalSettings: {
    pageColor: '#000000',
    containerColor: '#0a0a0a',
    borderColor: '#1f1f1f',
    fontFamily: "'Inter', sans-serif",
    headingColor: '#ffffff',
    textColor: '#a3a3a3',
    containerWidth: 600,
    previewText: 'Update',
  },
  selectedElement: null,

  // History management
  past: [],
  future: [],
  lastAction: null, // { type: 'undo' | 'redo' | 'action', name: string }

  // Internal helper to push to history
  pushToHistory: (actionName) => {
    const { layout, past } = get();
    // Max history depth 50
    const newPast = [...past, { layout: JSON.parse(JSON.stringify(layout)), actionName }].slice(-50);
    set({ past: newPast, future: [], lastAction: null });
  },

  undo: () => {
    const { past, layout, future } = get();
    if (past.length === 0) return;

    const previous = past[past.length - 1];
    const newPast = past.slice(0, past.length - 1);
    const currentClone = JSON.parse(JSON.stringify(layout));

    set({
      layout: previous.layout,
      past: newPast,
      future: [{ layout: currentClone, actionName: previous.actionName }, ...future],
      lastAction: { type: 'undo', name: previous.actionName },
      selectedElement: null
    });
  },

  redo: () => {
    const { future, layout, past } = get();
    if (future.length === 0) return;

    const next = future[0];
    const newFuture = future.slice(1);
    const currentClone = JSON.parse(JSON.stringify(layout));

    set({
      layout: next.layout,
      future: newFuture,
      past: [...past, { layout: currentClone, actionName: next.actionName }],
      lastAction: { type: 'redo', name: next.actionName },
      selectedElement: null
    });
  },

  // Actions
  setLayout: (newLayout, actionName = "Reorganized Layout") => {
    get().pushToHistory(actionName);
    set({ layout: newLayout });
  },

  setGlobalSettings: (settings) => set((state) => ({ globalSettings: { ...state.globalSettings, ...settings } })),
  setSelectedElement: (element) => set({ selectedElement: element }),

  updateBlock: (blockId, updates, actionName = "Updated Component") => {
    // Detect if this is just a single property update like text or color
    const customName = updates.width ? "Resized Component" : actionName;
    get().pushToHistory(customName);
    
    set((state) => {
      const newLayout = state.layout.map(row => ({
        ...row,
        columns: row.columns.map(col => ({
          ...col,
          blocks: col.blocks.map(block => block.id === blockId ? { ...block, ...updates } : block)
        }))
      }));
      return { layout: newLayout };
    });
  },

  updateRow: (rowId, updates) => {
    get().pushToHistory("Updated Row");
    set((state) => {
      const newLayout = state.layout.map(row => row.id === rowId ? { ...row, ...updates } : row);
      return { layout: newLayout };
    });
  },

  addBlock: (columnId, type, index) => {
    get().pushToHistory(`Added ${type.toUpperCase()}`);
    set((state) => {
      const newBlock = {
        id: `block-${Date.now()}`,
        type,
        align: 'left',
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        innerPaddingTop: 0,
        innerPaddingBottom: 0,
        innerPaddingLeft: 0,
        innerPaddingRight: 0,
      };

      if (type === 'heading') Object.assign(newBlock, { text: 'New Heading', color: state.globalSettings.headingColor, fontSize: 24, isBold: true });
      if (type === 'text') Object.assign(newBlock, { text: 'Enter your text here...', color: state.globalSettings.textColor, fontSize: 15 });
      if (type === 'image') Object.assign(newBlock, { src: '', width: 0, isHero: false, align: 'center' });
      if (type === 'button') Object.assign(newBlock, { text: 'Click Here', url: 'https://', align: 'center', bgColor: '#a855f7', color: '#ffffff', fontSize: 16, isBold: true });
      if (type === 'divider') Object.assign(newBlock, { paddingTop: 16, paddingBottom: 16 });
      if (type === 'social') Object.assign(newBlock, { socials: [], iconSize: 24, align: 'center' });

      const newLayout = state.layout.map(row => ({
        ...row,
        columns: row.columns.map(col => {
          if (col.id === columnId) {
            const blocks = [...col.blocks];
            blocks.splice(index !== undefined ? index : blocks.length, 0, newBlock);
            return { ...col, blocks };
          }
          return col;
        })
      }));
      return { layout: newLayout, selectedElement: { type: 'block', id: newBlock.id, parentId: columnId } };
    });
  },

  removeBlock: (blockId) => {
    get().pushToHistory("Removed Component");
    set((state) => {
      const newLayout = state.layout.map(row => ({
        ...row,
        columns: row.columns.map(col => ({
          ...col,
          blocks: col.blocks.filter(block => block.id !== blockId)
        }))
      }));
      return { layout: newLayout, selectedElement: state.selectedElement?.id === blockId ? null : state.selectedElement };
    });
  },

  addRow: (index) => {
    get().pushToHistory("Added Row");
    set((state) => {
      const newRow = {
        id: `row-${Date.now()}`,
        type: 'row',
        columns: [{ id: `col-${Date.now()}`, blocks: [] }]
      };
      const newLayout = [...state.layout];
      newLayout.splice(index !== undefined ? index : newLayout.length, 0, newRow);
      return { layout: newLayout, selectedElement: { type: 'row', id: newRow.id } };
    });
  },

  removeRow: (rowId) => {
    get().pushToHistory("Removed Row");
    set((state) => ({
      layout: state.layout.filter(row => row.id !== rowId),
      selectedElement: state.selectedElement?.id === rowId ? null : state.selectedElement
    }));
  },

  moveRow: (fromIndex, toIndex) => {
    get().pushToHistory("Moved Row");
    set((state) => {
      const newLayout = [...state.layout];
      const [movedRow] = newLayout.splice(fromIndex, 1);
      newLayout.splice(toIndex, 0, movedRow);
      return { layout: newLayout };
    });
  },

  // Template Management
  saveTemplate: async (name, db) => {
    const { layout, globalSettings } = useBuilderStore.getState();
    const { addDoc, collection } = await import("firebase/firestore");
    
    const newTemplate = {
      name,
      blocks: JSON.stringify(layout),
      globalSettings: JSON.stringify(globalSettings),
      createdAt: new Date().toISOString(),
    };
    
    try {
      const docRef = await addDoc(collection(db, "emailTemplates"), newTemplate);
      return { id: docRef.id, ...newTemplate };
    } catch (error) {
      console.error("Error saving template:", error);
      throw error;
    }
  },

  loadTemplate: (template) => {
    try {
      const parsedBlocks = typeof template.blocks === 'string' ? JSON.parse(template.blocks) : template.blocks;
      const globalSettings = typeof template.globalSettings === 'string' ? JSON.parse(template.globalSettings) : template.globalSettings;
      
      let finalLayout = [];

      if (parsedBlocks && parsedBlocks.length > 0 && parsedBlocks[0].type !== 'row') {
        console.log("Migrating older template format to new grid layout...");
        finalLayout = migrateOldTemplateToNewLayout(parsedBlocks);
      } else {
        finalLayout = parsedBlocks || [];
      }

      set({ layout: finalLayout, globalSettings, selectedElement: null, past: [], future: [] });
    } catch (error) {
      console.error("Error parsing template data:", error);
    }
  }
}));

export default useBuilderStore;
